/**
 * In-browser API mock for the `thresholds-carried-forward-amounts` prototype.
 *
 * Companion to src/lib/devAuth.ts. When the dev auth bypass is active (no real
 * Stytch keys / no Django backend on :8000), this patches window.fetch to serve
 * in-memory (localStorage-backed) data so the prototype is usable with no backend.
 *
 * Scope (intentionally NO revenue-statements feature — this prototype is about
 * contracts → royalty statement generation, thresholds and carried-forward amounts):
 *   - Contracts: list + create + detail (incl. read-only "scenario" fixtures that
 *     drive the create-statement panel with carried-forward + period banners).
 *   - Payees: list + detail.
 *   - Royalty statements: list + bulk-create (generate) + detail + html preview +
 *     row actions (mark sent / mark paid / delete). A few are pre-seeded as
 *     "already generated".
 *   - Label: GET/PATCH for the Settings view (default currency).
 * Any other API call returns an empty paginated response (GET) or {} (writes) so
 * the surrounding UI doesn't crash. Dev/demo convenience, NOT a backend replacement.
 *
 * Installed only from seedDevAuth() (gated on import.meta.env.DEV + placeholder
 * keys, or VITE_DEV_AUTH_BYPASS=true), so it is inert in normal production builds.
 */
import { API_BASE_URL } from "@/lib/api/core";

function jsonResponse(data: unknown, status = 200): Response {
  if (status === 204) return new Response(null, { status });
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

const emptyList = { results: [], count: 0, next: null, previous: null };

/** Wrap an array as a DRF-style paginated response ({ results, count, next, previous }). */
function paginated(results: unknown[]): object {
  return { results, count: results.length, next: null, previous: null };
}

// --- Payees ----------------------------------------------------------------
const DEV_PAYEES = [
  {
    id: "payee-adele",
    name: "Adele Laurie Blue Adkins",
    legal_name: "Adele Productions LLC",
    is_active: true,
    preferred_currency: "USD",
    country: "GB",
    city: "London",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-beyonce",
    name: "Beyoncé Giselle Knowles-Carter",
    legal_name: "Parkwood Entertainment",
    is_active: true,
    preferred_currency: "USD",
    country: "US",
    city: "Houston",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-james",
    name: "James Blake Litherland",
    legal_name: null,
    is_active: true,
    preferred_currency: "USD",
    country: "GB",
    city: "London",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  // Payees referenced by the seeded royalty statements (so the payee filter lists them).
  ...["Pezet", "Sokół", "O.S.T.R.", "Taco Hemingway", "Mata"].map((name, i) => ({
    id: ["payee-pezet", "payee-sokol", "payee-ostr", "payee-taco", "payee-mata"][i],
    name,
    legal_name: null,
    is_active: true,
    preferred_currency: "USD",
    country: "PL",
    city: "Warsaw",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  })),
];

function payeeName(id: string): string {
  return DEV_PAYEES.find((p) => p.id === id)?.name ?? id;
}

// --- Scenario contracts ----------------------------------------------------
// Read-only fixtures that drive the "Create royalty statement" panel. Each
// carries carried_statements (below-threshold balances carried from prior
// periods — the carried-forward total is their sum) and has_revenue/has_expenses
// + revenue_statements_preview to drive the amber period banners.
function scenarioContract(opts: {
  id: string;
  name: string;
  has_revenue: boolean;
  has_expenses: boolean;
  revenue_statements_preview: { name: string; period: string; status: "settled" | "draft" }[];
}) {
  return {
    id: opts.id,
    name: opts.name,
    status: "active",
    contract_type: "exclusive_license",
    currency: "USD",
    start_date: "2024-01-01",
    end_date: null,
    territory: "Worldwide",
    statement_period: "quarterly",
    starting_balance: null,
    // Per-contract minimum payout threshold (seeded from the label default in the
    // real flow). Drives carry-forward / below-threshold on the statements list.
    payment_threshold: "50.00",
    label: "dev-label",
    label_name: "Dev Label",
    party: "No.19 Music",
    tracks_count: 1,
    all_tracks_have_isrc: true,
    first_sound_recording_title: "Tripmastaz - While U Pretend (Original Mix)",
    can_delete: false,
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    payees: [
      {
        matched: true,
        payee: { id: "payee-adele", name: "Adele Laurie Blue Adkins" },
        share_percentage: 40,
        opening_balance: null,
        // Prior below-threshold balances carried from earlier periods. Each is
        // under the 50.00 threshold and they still sum below it (34.50) — that's
        // why they're carried, not paid out yet.
        carried_statements: [
          { name: "Spotify Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "18.50" },
          { name: "Apple Music Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "16.00" },
        ],
      },
      {
        matched: true,
        payee: { id: "payee-beyonce", name: "Beyoncé Giselle Knowles-Carter" },
        share_percentage: 35,
        opening_balance: null,
        carried_statements: [
          { name: "Spotify Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "22.00" },
        ],
      },
      {
        matched: true,
        payee: { id: "payee-james", name: "James Blake Litherland" },
        share_percentage: 25,
        opening_balance: null,
        carried_statements: [],
      },
    ],
    has_revenue: opts.has_revenue,
    has_expenses: opts.has_expenses,
    revenue_statements_preview: opts.revenue_statements_preview,
    related_recordings: [],
    related_releases: [],
  };
}

const SCENARIO_PERIOD = "Oct 1, 2025 - Dec 31, 2025";

const DEV_SCENARIO_CONTRACTS = [
  scenarioContract({
    id: "roy-scn-settled",
    name: "Back in Black Agreement 1481 — Settled revenue",
    has_revenue: true,
    has_expenses: true,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "settled" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-draft",
    name: "Back in Black Agreement 1482 — Draft revenue",
    has_revenue: true,
    has_expenses: true,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "draft" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-no-revenue",
    name: "Back in Black Agreement 1483 — No revenue",
    has_revenue: false,
    has_expenses: true,
    revenue_statements_preview: [],
  }),
  scenarioContract({
    id: "roy-scn-no-expenses",
    name: "Back in Black Agreement 1484 — No expenses",
    has_revenue: true,
    has_expenses: false,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "settled" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-none",
    name: "Back in Black Agreement 1485 — No revenue or expenses",
    has_revenue: false,
    has_expenses: false,
    revenue_statements_preview: [],
  }),
];

// --- User-created contracts (the "Add contract" flow) ----------------------
const CONTRACTS_STORE_KEY = "dev-mock-tcf-contracts";

function loadContracts(): any[] {
  try {
    return JSON.parse(localStorage.getItem(CONTRACTS_STORE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveContracts(list: any[]): void {
  localStorage.setItem(CONTRACTS_STORE_KEY, JSON.stringify(list));
}

// Build a fully-shaped Contract from a POST/PUT body, merging onto an existing
// record for updates.
function buildContract(body: any, existing?: any): any {
  const now = new Date().toISOString();
  const payees = Array.isArray(body?.payees)
    ? body.payees.map((p: any) => ({
        matched: true,
        payee: { id: p.payee, name: payeeName(p.payee) },
        share_percentage: p.share_percentage ?? null,
        opening_balance: p.opening_balance ?? null,
        carried_statements: [],
      }))
    : (existing?.payees ?? []);

  return {
    id: existing?.id ?? crypto.randomUUID(),
    created_at: existing?.created_at ?? now,
    updated_at: now,
    name: body?.name ?? existing?.name ?? "Untitled contract",
    contract_type: body?.contract_type ?? existing?.contract_type ?? "exclusive_license",
    status: body?.status ?? existing?.status ?? "draft",
    start_date: body?.start_date ?? existing?.start_date ?? null,
    end_date: body?.end_date ?? existing?.end_date ?? null,
    territory: body?.territory ?? existing?.territory ?? "Worldwide",
    currency: body?.currency ?? existing?.currency ?? "USD",
    label: body?.label ?? existing?.label ?? "dev-label",
    label_name: "Dev Label",
    party: existing?.party ?? "",
    label_share_percent:
      body?.label_share_percent != null
        ? String(body.label_share_percent)
        : (existing?.label_share_percent ?? null),
    payment_threshold: body?.payment_threshold ?? existing?.payment_threshold ?? null,
    advance_amount: body?.advance_amount ?? existing?.advance_amount ?? null,
    statement_period: body?.statement_period ?? existing?.statement_period ?? "quarterly",
    starting_balance: existing?.starting_balance ?? null,
    payees,
    releases_count: existing?.releases_count ?? 0,
    tracks_count: existing?.tracks_count ?? 0,
    all_tracks_have_isrc: true,
    first_sound_recording_title: null,
    can_delete: true,
    has_revenue: false,
    has_expenses: false,
    revenue_statements_preview: [],
    related_recordings: [],
    related_releases: [],
  };
}

function findContract(id: string): any | undefined {
  return (
    DEV_SCENARIO_CONTRACTS.find((c) => c.id === id) ?? loadContracts().find((c) => c.id === id)
  );
}

// --- Royalty statements ----------------------------------------------------
// Bump the version suffix when the seed shape changes so the browser re-seeds
// instead of serving a stale localStorage copy.
const ROYALTY_STORE_KEY = "dev-mock-tcf-royalty-v3";

const DEV_INVOICE_FILE =
  "data:text/csv;charset=utf-8,Description%2CAmount%0ANet%20royalties%20for%20period%2C4210.55";

function royaltyPayeeDetails(id: string, name: string, currency: string, country: string) {
  return {
    id,
    name,
    legal_name: `${name} LLC`,
    preferred_currency: currency,
    country,
    contact_email: `${name.split(" ")[0].toLowerCase()}@example.com`,
    is_active: true,
  };
}

function royaltyContractDetails(id: string, name: string, currency: string, threshold = "") {
  return {
    id,
    name,
    contract_type: "exclusive_license",
    status: "active",
    start_date: "2024-01-01",
    end_date: null,
    currency,
    payment_threshold: threshold,
  };
}

// "Several already generated" statements (all USD). Each (contract, payee) group
// exercises one minimum-payout-threshold state. `statement_balance` is each
// statement's OWN period amount ("Statement value"); the "Balance" column is the
// running cumulative (own + earlier unpaid carried), and the threshold is compared
// to that cumulative on the most recent unpaid statement.
//
//  1) "Test" (50.00) — Pezet: Q3 value 43.20 (carried) + Q4 value 61.40; balance grows
//     43.20 → 104.60 >= 50 → Q4 "Sent".
//  2) "Beat License" (50.00) — Sokół: Q1 value 18.00 + Q2 value 13.75; balance 18.00 →
//     31.75 < 50 → both "Carried forward", can't be marked paid.
//  3) "Sync Deal" (50.00) — O.S.T.R.: Q1 30.00 + Q2 40.00 (own periods), both Paid.
//  4) "Open Terms" (no threshold) — Taco Hemingway: 5.00, payable immediately, no chip.
//  5) "One-off" (50.00) — Mata:      62.00, payable immediately, no cascade.
const PEZET = royaltyPayeeDetails("payee-pezet", "Pezet", "USD", "PL");
const SOKOL = royaltyPayeeDetails("payee-sokol", "Sokół", "USD", "PL");
const OSTR = royaltyPayeeDetails("payee-ostr", "O.S.T.R.", "USD", "PL");
const TACO = royaltyPayeeDetails("payee-taco", "Taco Hemingway", "USD", "PL");
const MATA = royaltyPayeeDetails("payee-mata", "Mata", "USD", "PL");

const DEV_ROYALTY_STATEMENTS = [
  // 1) "Test" (50.00) — Pezet: Q4 value 61.40 + Q3 carried 43.20 → Q4 balance 104.60
  //    >= 50 → "Sent". Q3 below threshold → "Carried forward" (balance 43.20).
  {
    id: "roy-test-2",
    statement_no: "RS-2025-0102",
    payee: "payee-pezet",
    payee_details: PEZET,
    contract: "contract-test",
    contract_details: royaltyContractDetails("contract-test", "Test", "USD", "50.00"),
    period_start: "2025-10-01",
    period_end: "2025-12-31",
    statement_balance: "61.40",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2026-01-08T10:00:00Z",
    updated_at: "2026-01-08T10:00:00Z",
  },
  {
    id: "roy-test-1",
    statement_no: "RS-2025-0101",
    payee: "payee-pezet",
    payee_details: PEZET,
    contract: "contract-test",
    contract_details: royaltyContractDetails("contract-test", "Test", "USD", "50.00"),
    period_start: "2025-07-01",
    period_end: "2025-09-30",
    statement_balance: "43.20",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2025-10-08T10:00:00Z",
    updated_at: "2025-10-08T10:00:00Z",
  },
  // 2) "Beat License" (50.00) — Sokół: two periods both below threshold (18.00 + 13.75 =
  //    31.75 < 50) → both "Carried forward". Balance grows: 18.00 → 31.75. Statement
  //    value stays per-period (18.00 / 13.75). "Can't mark as paid" on both.
  {
    id: "roy-beat-2",
    statement_no: "RS-2026-0202",
    payee: "payee-sokol",
    payee_details: SOKOL,
    contract: "contract-beat",
    contract_details: royaltyContractDetails("contract-beat", "Beat License", "USD", "50.00"),
    period_start: "2026-04-01",
    period_end: "2026-06-30",
    statement_balance: "13.75",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2026-07-08T10:00:00Z",
    updated_at: "2026-07-08T10:00:00Z",
  },
  {
    id: "roy-beat-1",
    statement_no: "RS-2026-0201",
    payee: "payee-sokol",
    payee_details: SOKOL,
    contract: "contract-beat",
    contract_details: royaltyContractDetails("contract-beat", "Beat License", "USD", "50.00"),
    period_start: "2026-01-01",
    period_end: "2026-03-31",
    statement_balance: "18.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2026-04-08T10:00:00Z",
    updated_at: "2026-04-08T10:00:00Z",
  },
  // 3) "Sync Deal" (50.00) — O.S.T.R.: both already Paid (history).
  {
    id: "roy-sync-1",
    statement_no: "RS-2025-0301",
    payee: "payee-ostr",
    payee_details: OSTR,
    contract: "contract-sync",
    contract_details: royaltyContractDetails("contract-sync", "Sync Deal", "USD", "50.00"),
    period_start: "2025-01-01",
    period_end: "2025-12-31",
    statement_balance: "30.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "paid",
    created_at: "2026-01-10T10:00:00Z",
    updated_at: "2026-02-01T10:00:00Z",
  },
  {
    id: "roy-sync-2",
    statement_no: "RS-2026-0302",
    payee: "payee-ostr",
    payee_details: OSTR,
    contract: "contract-sync",
    contract_details: royaltyContractDetails("contract-sync", "Sync Deal", "USD", "50.00"),
    period_start: "2026-01-01",
    period_end: "2026-12-31",
    statement_balance: "40.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "paid",
    created_at: "2026-12-31T10:00:00Z",
    updated_at: "2027-01-15T10:00:00Z",
  },
  // 4) "Open Terms" (no threshold) — Taco Hemingway: payable immediately, no chip.
  {
    id: "roy-open-1",
    statement_no: "RS-2026-0401",
    payee: "payee-taco",
    payee_details: TACO,
    contract: "contract-open",
    contract_details: royaltyContractDetails("contract-open", "Open Terms", "USD", ""),
    period_start: "2026-03-01",
    period_end: "2026-03-31",
    statement_balance: "5.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2026-04-05T10:00:00Z",
    updated_at: "2026-04-05T10:00:00Z",
  },
  // 5) "One-off" (50.00) — Mata: single statement meets threshold, payable, no cascade.
  {
    id: "roy-oneoff-1",
    statement_no: "RS-2026-0501",
    payee: "payee-mata",
    payee_details: MATA,
    contract: "contract-oneoff",
    contract_details: royaltyContractDetails("contract-oneoff", "One-off", "USD", "50.00"),
    period_start: "2026-04-01",
    period_end: "2026-04-30",
    statement_balance: "62.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2026-05-06T10:00:00Z",
    updated_at: "2026-05-06T10:00:00Z",
  },
];

function loadRoyalty(): any[] {
  const raw = localStorage.getItem(ROYALTY_STORE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* reseed */
    }
  }
  localStorage.setItem(ROYALTY_STORE_KEY, JSON.stringify(DEV_ROYALTY_STATEMENTS));
  return DEV_ROYALTY_STATEMENTS;
}

function saveRoyalty(list: any[]): void {
  localStorage.setItem(ROYALTY_STORE_KEY, JSON.stringify(list));
}

let royaltySeq = 1000;
function nextStatementNo(): string {
  royaltySeq += 1;
  return `RS-2026-${String(royaltySeq).padStart(4, "0")}`;
}

// Generate one statement per payee for the selected contract + period.
function bulkCreateRoyalty(body: any): any[] {
  const contract = findContract(body?.contract);
  const period_start = body?.period_start ?? null;
  const period_end = body?.period_end ?? null;
  const payeeIds: string[] = Array.isArray(body?.payees) ? body.payees : [];
  const list = loadRoyalty();
  const created = payeeIds.map((payeeId, i) => {
    const contractPayee = contract?.payees?.find((p: any) => p.payee.id === payeeId);
    const carried = (contractPayee?.carried_statements ?? []).reduce(
      (sum: number, s: any) => sum + (Number.parseFloat(s.amount) || 0),
      0,
    );
    const share = (contractPayee?.share_percentage ?? 100) / 100;
    // Statement balance = prior carried-forward balance + this period's earnings.
    // With a payout threshold set, the period stays small enough that the running
    // balance remains *below* it, so the statement is carried forward (not
    // payable) once sent — until a later period finally crosses the threshold.
    // With no threshold set, earnings are a normal (payable) figure.
    const threshold = Number.parseFloat(contract?.payment_threshold ?? "") || 0;
    const periodEarnings = threshold > 0 ? threshold * 0.6 * share : 4000 * share;
    const balance = (carried + periodEarnings).toFixed(2);
    const name = payeeName(payeeId);
    const country = DEV_PAYEES.find((p) => p.id === payeeId)?.country ?? "US";
    return {
      id: crypto.randomUUID(),
      statement_no: nextStatementNo(),
      payee: payeeId,
      payee_details: royaltyPayeeDetails(payeeId, name, "USD", country),
      contract: contract?.id ?? body?.contract ?? null,
      contract_details: royaltyContractDetails(
        contract?.id ?? "contract",
        contract?.name ?? "Contract",
        "USD",
        contract?.payment_threshold ?? "",
      ),
      period_start,
      period_end,
      statement_balance: balance,
      currency: "USD",
      file: DEV_INVOICE_FILE,
      file_url: DEV_INVOICE_FILE,
      created_by: "dev@local.test",
      status: "draft",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _seq: i,
    };
  });
  saveRoyalty([...created, ...list]);
  return created.map(({ _seq, ...s }) => s);
}

function royaltyStatementHtml(s: any): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font-family:ui-sans-serif,system-ui,sans-serif;color:#111;padding:32px;max-width:720px;margin:0 auto}
    h1{font-size:20px;margin:0 0 4px} .muted{color:#666;font-size:13px}
    table{width:100%;border-collapse:collapse;margin-top:24px;font-size:14px}
    th,td{text-align:left;padding:8px 0;border-bottom:1px solid #eee} td.r,th.r{text-align:right}
    .total{font-weight:600;font-size:16px}
  </style></head><body>
    <h1>Royalty statement ${s.statement_no ?? ""}</h1>
    <div class="muted">${s.payee_details?.name ?? ""} · ${s.contract_details?.name ?? ""}</div>
    <div class="muted">Period: ${s.period_start ?? ""} – ${s.period_end ?? ""}</div>
    <table>
      <tr><th>Description</th><th class="r">Amount</th></tr>
      <tr><td>Net royalties for period</td><td class="r">${s.statement_balance ?? "0.00"} ${s.currency ?? "USD"}</td></tr>
      <tr><td class="total">Amount due</td><td class="r total">${s.statement_balance ?? "0.00"} ${s.currency ?? "USD"}</td></tr>
    </table>
  </body></html>`;
}

function previewHtml(query: URLSearchParams): string {
  const start = query.get("period_start") ?? query.get("start_date") ?? "";
  const end = query.get("period_end") ?? query.get("end_date") ?? "";
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font-family:ui-sans-serif,system-ui,sans-serif;color:#111;padding:24px}
    h1{font-size:18px;margin:0 0 4px} .muted{color:#666;font-size:13px}
  </style></head><body>
    <h1>Statement preview</h1>
    <div class="muted">Period: ${start} – ${end}</div>
    <p>This is a mock preview generated for the prototype. The real statement is
    produced from settled revenue, expenses and any carried-forward balances.</p>
  </body></html>`;
}

// --- Label (Settings view) -------------------------------------------------
const LABEL_STORE_KEY = "dev-mock-tcf-label";

function loadLabel(): any {
  const raw = localStorage.getItem(LABEL_STORE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* reseed */
    }
  }
  const label = {
    id: "dev-label",
    name: "Dev Label",
    currency: "USD",
    // Empty by default → "No minimum" (nothing blocked). Set it in Settings.
    payment_threshold: "",
    can_update_currency: true,
    country: "US",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
  };
  localStorage.setItem(LABEL_STORE_KEY, JSON.stringify(label));
  return label;
}

function saveLabel(label: any): void {
  localStorage.setItem(LABEL_STORE_KEY, JSON.stringify(label));
}

// --- Reset (prototype affordance) ----------------------------------------
// Wipes every in-memory store this prototype owns so the next load re-seeds
// from defaults: contracts, royalty statements and the label. Also clears the
// persisted org/label selection so the label currency/threshold re-initialise
// from the seed instead of a stale saved value.
export function resetPrototype(): void {
  for (const key of [CONTRACTS_STORE_KEY, ROYALTY_STORE_KEY, LABEL_STORE_KEY]) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem("organization-selection");
}

// Returns a Response for handled routes, or null to fall through to real fetch.
function route(method: string, path: string, body: any, query: URLSearchParams): Response | null {
  const trimmed = path.replace(/\/+$/, "");

  // --- Label (Settings) ----------------------------------------------------
  if (trimmed === "/accounts/labels" && method === "GET") {
    return jsonResponse(paginated([loadLabel()]));
  }
  if (trimmed.startsWith("/accounts/labels/")) {
    const id = trimmed.slice("/accounts/labels/".length);
    const label = loadLabel();
    if (id !== label.id) return jsonResponse({ detail: "Not found." }, 404);
    if (method === "GET") return jsonResponse(label);
    if (method === "PATCH" || method === "PUT") {
      const updated = { ...label, ...body, updated_at: new Date().toISOString() };
      saveLabel(updated);
      return jsonResponse(updated);
    }
  }

  // --- Payees (match BEFORE /contracts/:id) --------------------------------
  if (trimmed === "/contracts/payees" && method === "GET") {
    return jsonResponse(paginated(DEV_PAYEES));
  }
  if (trimmed.startsWith("/contracts/payees/")) {
    const id = trimmed.slice("/contracts/payees/".length);
    const found = DEV_PAYEES.find((p) => p.id === id);
    return found ? jsonResponse(found) : jsonResponse({ detail: "Not found." }, 404);
  }

  // --- Royalty statements --------------------------------------------------
  if (trimmed === "/revenue/royalty-statements/bulk-create" && method === "POST") {
    return jsonResponse(bulkCreateRoyalty(body), 201);
  }
  if (trimmed === "/revenue/royalty-statements/preview/html" && method === "GET") {
    return jsonResponse({ html: previewHtml(query) });
  }
  if (trimmed === "/revenue/royalty-statements" && method === "GET") {
    let list = loadRoyalty();
    const q = (query.get("search") || query.get("q") || "").toLowerCase();
    if (q) {
      list = list.filter((s) =>
        `${s.statement_no} ${s.payee_details?.name ?? ""} ${s.contract_details?.name ?? ""}`
          .toLowerCase()
          .includes(q),
      );
    }
    const statusParam = query.get("status");
    if (statusParam) {
      const allowed = new Set(statusParam.split(","));
      list = list.filter((s) => allowed.has(s.status));
    }
    const payeesParam = query.get("payees");
    if (payeesParam) {
      const allowed = new Set(payeesParam.split(","));
      list = list.filter((s) => allowed.has(s.payee));
    }
    const after = query.get("period_start_after");
    const before = query.get("period_end_before");
    if (after) list = list.filter((s) => (s.period_start ?? "") >= after);
    if (before) list = list.filter((s) => (s.period_end ?? "") <= before);
    const ordering = query.get("ordering");
    if (ordering) {
      const desc = ordering.startsWith("-");
      const field = desc ? ordering.slice(1) : ordering;
      list = [...list].sort((a, b) => {
        const cmp = String(a[field] ?? "").localeCompare(String(b[field] ?? ""), undefined, {
          numeric: true,
        });
        return desc ? -cmp : cmp;
      });
    }
    return jsonResponse(paginated(list));
  }
  if (trimmed.startsWith("/revenue/royalty-statements/")) {
    const rest = trimmed.slice("/revenue/royalty-statements/".length);
    const [id, sub] = rest.split("/");
    const list = loadRoyalty();
    const idx = list.findIndex((s) => s.id === id);

    if (sub === "html" && method === "GET") {
      return idx === -1
        ? jsonResponse({ detail: "Not found." }, 404)
        : jsonResponse({ html: royaltyStatementHtml(list[idx]) });
    }
    if (sub === "mark-sent" && method === "POST") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      list[idx] = { ...list[idx], status: "sent", updated_at: new Date().toISOString() };
      saveRoyalty(list);
      return jsonResponse(list[idx]);
    }
    if (sub === "mark-paid" && method === "POST") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      list[idx] = { ...list[idx], status: "paid", updated_at: new Date().toISOString() };
      saveRoyalty(list);
      return jsonResponse(list[idx]);
    }
    if (!sub) {
      if (method === "GET") {
        return idx === -1 ? jsonResponse({ detail: "Not found." }, 404) : jsonResponse(list[idx]);
      }
      if (method === "PATCH") {
        if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
        list[idx] = { ...list[idx], ...body, updated_at: new Date().toISOString() };
        saveRoyalty(list);
        return jsonResponse(list[idx]);
      }
      if (method === "DELETE") {
        if (idx !== -1) {
          list.splice(idx, 1);
          saveRoyalty(list);
        }
        return jsonResponse(null, 204);
      }
    }
  }

  // --- Contracts -----------------------------------------------------------
  if (trimmed === "/contracts") {
    if (method === "GET") {
      let list = [...DEV_SCENARIO_CONTRACTS, ...loadContracts()];
      const q = query.get("q")?.toLowerCase();
      if (q)
        list = list.filter((c) =>
          String(c.name ?? "")
            .toLowerCase()
            .includes(q),
        );
      const statusParam = query.get("status");
      if (statusParam) {
        const allowed = new Set(statusParam.split(","));
        list = list.filter((c) => allowed.has(c.status));
      }
      const ordering = query.get("ordering");
      if (ordering) {
        const desc = ordering.startsWith("-");
        const field = desc ? ordering.slice(1) : ordering;
        list = [...list].sort((a, b) => {
          const cmp = String(a[field] ?? "").localeCompare(String(b[field] ?? ""));
          return desc ? -cmp : cmp;
        });
      }
      return jsonResponse({ results: list, count: list.length, next: null, previous: null });
    }
    if (method === "POST") {
      const list = loadContracts();
      const created = buildContract(body);
      list.unshift(created);
      saveContracts(list);
      return jsonResponse(created, 201);
    }
  }
  if (trimmed.startsWith("/contracts/")) {
    const id = trimmed.slice("/contracts/".length);

    const scenario = DEV_SCENARIO_CONTRACTS.find((c) => c.id === id);
    if (scenario && method === "GET") return jsonResponse(scenario);

    const list = loadContracts();
    const idx = list.findIndex((c) => c.id === id);

    if (method === "GET") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      return jsonResponse(list[idx]);
    }
    if (method === "PUT" || method === "PATCH") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      const updated = buildContract(body, list[idx]);
      list[idx] = updated;
      saveContracts(list);
      return jsonResponse(updated);
    }
    if (method === "DELETE") {
      if (idx !== -1) {
        list.splice(idx, 1);
        saveContracts(list);
      }
      return jsonResponse(null, 204);
    }
  }

  // Unhandled API route: keep the UI alive with a benign empty payload.
  console.warn(`[tcf-mock] Unhandled ${method} ${path} — returning empty response.`);
  return method === "GET" ? jsonResponse(emptyList) : jsonResponse({});
}

export function installDevApiMock(): void {
  const w = window as unknown as { __devApiMockInstalled?: boolean };
  if (w.__devApiMockInstalled) return;
  w.__devApiMockInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === "string" ? input : input instanceof Request ? input.url : String(input);

    // Only intercept calls to our API base; everything else (Vite HMR, assets) passes through.
    if (!url.startsWith(API_BASE_URL)) return originalFetch(input, init);

    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const [path, queryString = ""] = url.slice(API_BASE_URL.length).split("?");
    const query = new URLSearchParams(queryString);

    let body: any;
    const rawBody = init?.body;
    if (typeof rawBody === "string") {
      try {
        body = JSON.parse(rawBody);
      } catch {
        body = undefined;
      }
    } else if (rawBody instanceof FormData) {
      body = {};
      rawBody.forEach((value, key) => {
        body[key] = value instanceof File ? value.name : value;
      });
    }

    const handled = route(method, path, body, query);
    return handled ?? originalFetch(input, init);
  };

  console.warn(
    "[tcf-mock] In-browser contracts + royalty + settings API mock active (no backend).",
  );
}
